import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";

const COLLAGE = [
  {
    src: "https://images.unsplash.com/photo-1551028719-00167b16eac5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwamFja2V0JTIwbGVhdGhlciUyMGZhc2hpb24lMjBtaW5pbWFsJTIwZGFyayUyMHBob3RvZ3JhcGh5JTIwcHJvZHVjdHxlbnwwfHx8fDE3ODkxNzA2NTl8MA&ixlib=rb-4.1.0&q=85",
    alt: "Vintage leather jacket",
    cls: "left-0 top-4 w-52 -rotate-6 md:w-60",
    dur: 5,
  },
  {
    src: "https://images.unsplash.com/photo-1623854156816-4c4fc355ffc7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHx2aW50YWdlJTIwamFja2V0JTIwbGVhdGhlciUyMGZhc2hpb24lMjBtaW5pbWFsJTIwZGFyayUyMHBob3RvZ3JhcGh5JTIwcHJvZHVjdHxlbnwwfHx8fDE3ODkxNzA2NTl8MA&ixlib=rb-4.1.0&q=85",
    alt: "Brown suede jacket",
    cls: "right-0 top-44 w-40 rotate-3 md:w-48",
    dur: 6.5,
  },
  {
    src: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&q=80",
    alt: "Clothing rack with hanging garments",
    cls: "bottom-0 left-16 w-36 rotate-2 md:w-44",
    dur: 7.5,
  },
];

const LINES = [
  { text: "THEIR TRASH", cls: "text-[#F5F2EB]" },
  { text: "IS YOUR TREASURE.", cls: "text-[#F5F2EB]" },
  { text: "JFLY®", cls: "text-[#FF3B1F]" },
];

const CHIPS = ["1 OF 1", "NO RESTOCKS", "100% PRE-LOVED"];

export const Hero = () => {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden pt-16">
      <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full border-2 border-[#F5F2EB]/10" />
      <div className="pointer-events-none absolute -left-20 bottom-16 h-64 w-64 rounded-full bg-[#FFD23F]/50" />

      <div className="relative z-10 grid w-full grid-cols-1 items-center gap-12 px-6 pb-24 pt-16 md:px-12 lg:grid-cols-12 lg:px-20">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-overline"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mb-8 inline-block rounded-full border-2 border-[#F5F2EB] bg-[#141414] px-4 py-1.5 text-[10px] font-mono font-medium uppercase tracking-[0.25em]"
          >
            Archival & resale apparel — one of one
          </motion.p>

          <h1 data-testid="hero-title" className="font-serif font-black uppercase leading-[0.9] tracking-tight text-[clamp(3rem,10vw,8rem)]">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.35 + i * 0.16, duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.7 }}
            className="mt-8 max-w-md text-sm leading-relaxed text-[#F5F2EB]/70"
          >
            A rotating rack of pre-loved clothing — hand-picked, cleaned, and re-homed. When a piece is gone, it is gone forever.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-browse-button"
              onClick={() => go("shop")}
              className="rounded-full border-2 border-[#F5F2EB] bg-[#FF3B1F] px-8 py-3 text-xs font-mono font-medium uppercase tracking-[0.2em] text-white shadow-[4px_4px_0_#F5F2EB] transition-transform hover:-translate-y-0.5"
            >
              Shop the drop
            </button>
            <button
              data-testid="hero-contact-button"
              onClick={() => go("contact")}
              className="rounded-full border-2 border-[#F5F2EB] bg-[#141414] px-8 py-3 text-xs font-mono font-medium uppercase tracking-[0.2em] text-[#F5F2EB] transition-all hover:-translate-y-0.5 hover:shadow-[4px_4px_0_#F5F2EB]"
            >
              Get in touch
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-2"
          >
            {CHIPS.map((chip, i) => (
              <motion.span
                key={chip}
                animate={{ y: [0, -6, 0] }}
                transition={{ repeat: Infinity, duration: 3.5 + i, ease: "easeInOut" }}
                className={`rounded-full border-2 border-[#F5F2EB] px-4 py-1.5 text-[10px] font-mono font-semibold uppercase tracking-[0.2em] ${
                  i === 1 ? "bg-[#FF3B1F] text-white" : "bg-[#141414] text-[#F5F2EB]"
                } ${i === 0 ? "-rotate-2" : i === 2 ? "rotate-2" : ""}`}
              >
                {chip}
              </motion.span>
            ))}
          </motion.div>
        </div>

        <div className="relative hidden h-[520px] lg:col-span-5 lg:block" data-testid="hero-collage">
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.5, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="absolute left-16 top-40 flex h-32 w-32 items-center justify-center"
          >
            <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full animate-[spin_14s_linear_infinite]">
              <defs>
                <path id="badge-circle" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
              </defs>
              <text className="fill-[#F5F2EB] font-mono text-[9px] font-semibold uppercase tracking-[0.2em]">
                <textPath href="#badge-circle">pre-loved • one of one • jfly •</textPath>
              </text>
            </svg>
            <span className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-[#F5F2EB] bg-[#FF3B1F] text-white">
              <ArrowDown size={18} />
            </span>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2">
        <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#F5F2EB]/50">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }} className="text-[#FF3B1F]">
          <ArrowDown size={16} />
        </motion.span>
      </div>
    </section>
  );
};
